import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Button,
  Card,
  CardList,
  Col,
  Copy,
  Divider,
  Flex,
  Grid,
  Page,
  Row,
  Tag,
  Tooltip,
  Typography,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { SubLayout } from "@/components/SubLayout";
import { Add, CuToman, FiRefreshCcw, LineChart } from "@payping/icons";
import { useAsync } from "react-use";
import {
  getDepositBalance,
  getStatementsList,
  getTransferRequestList,
  getUserKhavarmianehStatus,
} from "@payping/redux/actions";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
import { AgreementStateEnum, DepositStatementActionEnum, TransferStateEnum } from "@payping/api";
import { dateFormats, jalaliNow, toJalali } from "@payping/utils/dateUtils";
import { Link } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import KhavarmianehCard from "@/pages/DigitalAccount/Khavarmianeh/StatusCard";
import Capability from "@/pages/DigitalAccount/capability";
import SamanCard from "@/pages/DigitalAccount/Saman/StatusCard";
import { reportId } from "@/assets/googleTrackIds";
import { BankTypeEnum } from "@payping/api/app/api/digitalAccountBankAgreementsTypes";

const YourAccount = () => {
  const { t } = useAppTranslation();
  const { bankAgreement } = useAppSelector((state) => state.digitalAccount);
  const { statementsList } = useAppSelector((state) => state.digitalAccount.saman);
  const { transferRequestList } = useAppSelector((state) => state.digitalAccount.saman);
  const { status: khavarmianehStatus } = useAppSelector((state) => state.digitalAccount.khavarmianeh);
  const dispatch = useAppDispatch();
  const { md } = Grid.useBreakpoint();

  const confirmedSamanAgreement = bankAgreement.find(
    (agreement) =>
      agreement.bankType === BankTypeEnum.Saman && agreement.agreementInfo?.state === AgreementStateEnum.Confirmed,
  );

  useAsync(async () => {
    if (confirmedSamanAgreement) {
      await dispatch(getDepositBalance());
    }
  }, [dispatch, confirmedSamanAgreement]);

  const { loading: loadingRequest, retry: retryRequest } = useMessagedAsyncRetry(async () => {
    if (confirmedSamanAgreement) {
      await dispatch(
        getTransferRequestList({
          pageNumber: 1,
          pageSize: 3,
          state: TransferStateEnum.Accepted,
          // startDate: toUtc({
          //   convertTime: false,
          //   dateFormat: dateFormats.standard.utcDateTime,
          // }).string,
          // endDate: toUtc({
          //   convertTime: false,
          //   dateFormat: dateFormats.standard.utcDateTime,
          // }).string,
        }),
      );
    }
  }, [dispatch, confirmedSamanAgreement]);

  const { loading: loadingStatements, retry: retryStatements } = useMessagedAsyncRetry(async () => {
    if (confirmedSamanAgreement) {
      await dispatch(
        getStatementsList({
          pageSize: 5,
          pageNumber: 1,
          startDate: jalaliNow({
            manipulate: [{ args: [1, "month"], method: "subtract" }],
            toFormat: dateFormats.standard.utcDateTime,
          })
            .object.startOf("day")
            .toDate(),
          endDate: jalaliNow({ toFormat: dateFormats.standard.utcDateTime }).object.toDate(),
        }),
      );
    }
  }, [dispatch, confirmedSamanAgreement]);

  useAsync(async () => {
    const confirmedKhavarAgreement = bankAgreement.find(
      (agreement) =>
        agreement.bankType === BankTypeEnum.KhavarMiyane &&
        agreement.agreementInfo?.state === AgreementStateEnum.Confirmed,
    );
    if (confirmedKhavarAgreement) await dispatch(getUserKhavarmianehStatus());
  }, [dispatch, bankAgreement]);

  const data: ListCardProps["dataSource"] = (statementsList.statements ?? []).map((statements) => ({
    actions: [
      <Flex align="center">
        <Typography.Text
          key={1}
        >{`${formatLocalizedNumber({ value: Math.trunc((statements.transferAmount || 0) / 10) })}`}</Typography.Text>
        <CuToman width={24} height={24} />
      </Flex>,
    ],
    image: md
      ? {
          type: "icon",
          content: [
            <LineChart
              key={1}
              color={statements.action === DepositStatementActionEnum.CREDIT ? "green" : "red"}
              width={16}
            />,
          ],
        }
      : undefined,
    content: [
      <Typography.Text key={1} type={"secondary"} ellipsis>
        <Tooltip title={statements.description}>{statements.description}</Tooltip>
      </Typography.Text>,
    ],
    title: {
      primary: (
        <Flex gap={16}>
          {!md ? (
            <LineChart
              key={1}
              color={statements.action === DepositStatementActionEnum.CREDIT ? "green" : "red"}
              width={16}
            />
          ) : null}
          <Typography.Text>
            {statements.action === DepositStatementActionEnum.CREDIT
              ? t("digitalAccount.yourAccount.credit")
              : t("digitalAccount.yourAccount.debit")}
          </Typography.Text>
          <Typography.Text>
            {
              toJalali({
                date: statements.date,
                dateFormat: dateFormats.standard.utcDateTime,
                toFormat: dateFormats.ui.jalaliDate,
              }).string
            }
          </Typography.Text>
        </Flex>
      ),
    },
    key: statements.referenceNumber || "",
  }));

  const dataTransfer: ListCardProps["dataSource"] = (transferRequestList.items ?? [])?.map((transfers) => ({
    actions: [
      <Flex gap={"small"} key={1}>
        {transfers.state === TransferStateEnum.Accepted ? (
          <Tag color={"warning"}>{t("digitalAccount.yourAccount.inProgress")}</Tag>
        ) : null}
        <Flex align="center">
          <Typography.Text
            type={"secondary"}
          >{`${formatLocalizedNumber({ value: Math.trunc((transfers.amount || 0) / 10) })}`}</Typography.Text>
          <CuToman width={24} height={24} />
        </Flex>
      </Flex>,
    ],
    content: [
      <Flex key={1} gap={"small"}>
        <Typography.Text
          type={"secondary"}
        >{`${transfers.destination?.ownerName} : ${transfers.destination?.iban}`}</Typography.Text>
        <Copy showIcon text={transfers.destination?.iban || ""} />
      </Flex>,
    ],
    title: {
      primary: (
        <Typography.Text>
          {
            toJalali({
              date: transfers.operationCreationDateTime,
              dateFormat: dateFormats.standard.utcDateTime,
              toFormat: dateFormats.ui.jalaliDate,
            }).string
          }
        </Typography.Text>
      ),
    },
    key: transfers.id || "",
  }));

  return (
    <Page>
      <SubLayout>
        <Flex vertical gap={32}>
          <Row gutter={[0, 16]} justify={"center"}>
            {bankAgreement.length ? (
              <Col xs={24} lg={bankAgreement.length === 1 ? 24 : 12}>
                <SamanCard />
              </Col>
            ) : (
              <Col xs={24} lg={12}>
                <Card.Ant className="!tw-flex tw-h-full !tw-items-center !tw-justify-center">
                  <Flex vertical gap={16}>
                    <Typography.Title className="!tw-flex !tw-justify-center" level={5}>
                      {t("digitalAccount.yourAccount.openingAccount")}
                    </Typography.Title>
                    <Typography.Text type={"secondary"}>
                      {t("digitalAccount.yourAccount.openingAccountDesc")}
                    </Typography.Text>
                    <div className="!tw-flex !tw-justify-center">
                      <Link to={DashboardRoutes.digitalAccount.agreement.url}>
                        <Button className={"tw-w-fit"} icon={<Add width={14} height={14} />} type={"default"}>
                          {t("digitalAccount.yourAccount.addAccount")}
                        </Button>
                      </Link>
                    </div>
                  </Flex>
                </Card.Ant>
              </Col>
            )}
            {/* eslint-disable-next-line no-nested-ternary */}
            {khavarmianehStatus ? (
              khavarmianehStatus.status === "notFound" ? (
                <Col xs={24} lg={12}>
                  <Card.Ant className="!tw-flex tw-h-full !tw-items-center !tw-justify-center">
                    <Flex vertical gap={16}>
                      <Typography.Title className="!tw-flex !tw-justify-center" level={5}>
                        {t("digitalAccount.yourAccount.openingAccount")}
                      </Typography.Title>
                      <Typography.Text type={"secondary"}>
                        {t("digitalAccount.yourAccount.openingAccountDesc")}
                      </Typography.Text>
                      <div className="!tw-flex !tw-justify-center">
                        <Link to={DashboardRoutes.digitalAccount.jointAccount.khavarmianeh?.url}>
                          <Button className={"tw-w-fit"} icon={<Add width={14} height={14} />} type={"default"}>
                            {t("digitalAccount.yourAccount.addAccount")}
                          </Button>
                        </Link>
                      </div>
                    </Flex>
                  </Card.Ant>
                </Col>
              ) : (
                <Col xs={24} lg={bankAgreement.length === 1 ? 24 : 12}>
                  <KhavarmianehCard status={khavarmianehStatus} />
                </Col>
              )
            ) : null}
            {dataTransfer?.length ? (
              <Col xs={24}>
                <Flex vertical gap={"small"}>
                  {md ? (
                    <div className={"tw-flex tw-justify-between"}>
                      <Flex gap={16} className={"tw-flex !tw-items-center"}>
                        <Typography.Title level={5} className="!tw-mb-0">
                          {t("digitalAccount.yourAccount.requestsTransferList")}
                        </Typography.Title>
                        <Link to={DashboardRoutes.digitalAccount.saman.request.url}>
                          <Button type={"link"} className={"tw-p-0"} data-google-track={reportId}>
                            {t("showAll")}
                          </Button>
                        </Link>
                      </Flex>
                      <Button
                        type={"default"}
                        icon={<FiRefreshCcw width={14} height={14} />}
                        onClick={() => retryRequest()}
                      >
                        {t("update")}
                      </Button>
                    </div>
                  ) : (
                    <Flex vertical gap={"middle"}>
                      <Typography.Title level={5} className="!tw-mb-0">
                        {t("digitalAccount.yourAccount.requestsTransferList")}
                      </Typography.Title>
                      <div className={"tw-flex tw-justify-between"}>
                        <Link to={DashboardRoutes.digitalAccount.saman.request.url}>
                          <Button type={"link"} className={"tw-p-0"} data-google-track={reportId}>
                            {t("showAll")}
                          </Button>
                        </Link>
                        <Button
                          type={"default"}
                          icon={<FiRefreshCcw width={14} height={14} />}
                          onClick={() => retryRequest()}
                        >
                          {t("update")}
                        </Button>
                      </div>
                    </Flex>
                  )}
                  <CardList
                    grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1, column: 0 }}
                    dataSource={dataTransfer}
                    loading={loadingRequest}
                    mode={md ? DisplayModeEnum.List : DisplayModeEnum.Card}
                  />
                </Flex>
              </Col>
            ) : null}
            {dataTransfer?.length && data.length ? <Divider /> : null}
            {data.length ? (
              <Col xs={24}>
                <Flex vertical gap={"small"}>
                  {md ? (
                    <div className="tw-flex tw-justify-between">
                      <Flex gap={16} className="tw-flex !tw-items-center">
                        <Typography.Title level={5} className="!tw-mb-0">
                          {t("digitalAccount.yourAccount.accountTurnOverSaman")}
                        </Typography.Title>
                        <Typography.Text type={"secondary"}>
                          {t("digitalAccount.yourAccount.theRecent31Days")}
                        </Typography.Text>
                        <Link to={DashboardRoutes.digitalAccount.saman.turnOver.url}>
                          <Button type={"link"} className={"tw-p-0"} data-google-track={reportId}>
                            {t("showAll")}
                          </Button>
                        </Link>
                      </Flex>
                      <Button
                        type={"default"}
                        icon={<FiRefreshCcw width={14} height={14} />}
                        onClick={() => retryStatements()}
                      >
                        {t("update")}
                      </Button>
                    </div>
                  ) : (
                    <Flex vertical gap={"middle"}>
                      <Flex gap={16} className="tw-flex !tw-items-center">
                        <Typography.Title level={5} className="!tw-mb-0">
                          {t("digitalAccount.yourAccount.accountTurnOverSaman")}
                        </Typography.Title>
                        <Typography.Text type={"secondary"}>
                          {t("digitalAccount.yourAccount.theRecent31Days")}
                        </Typography.Text>
                      </Flex>
                      <div className={"tw-flex tw-justify-between"}>
                        <Link to={DashboardRoutes.digitalAccount.saman.turnOver.url}>
                          <Button type={"link"} className={"tw-p-0"} data-google-track={reportId}>
                            {t("showAll")}
                          </Button>
                        </Link>
                        <Button
                          type={"default"}
                          icon={<FiRefreshCcw width={14} height={14} />}
                          onClick={() => retryStatements()}
                        >
                          {t("update")}
                        </Button>
                      </div>
                    </Flex>
                  )}
                  <CardList
                    grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1, column: 0 }}
                    dataSource={data}
                    loading={loadingStatements}
                    mode={md ? DisplayModeEnum.List : DisplayModeEnum.Card}
                  />
                </Flex>
              </Col>
            ) : null}
            <Col xs={24}>
              <Card.Ant>
                <Flex vertical gap={32}>
                  <Typography.Text className="tw-justify-start" type={"secondary"}>
                    {t("digitalAccount.capability.title")}
                  </Typography.Text>
                  <Capability />
                </Flex>
              </Card.Ant>
            </Col>
          </Row>
        </Flex>
      </SubLayout>
    </Page>
  );
};

export default YourAccount;
